import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";

// The answer's paragraph lines (`text-gpt-answer`, 15/22): the last one stops
// short, the way a paragraph's closing line does. Widths as a percentage of the
// column so the silhouette holds on every screen width.
const ANSWER_LINE_WIDTHS = ["100%", "100%", "100%", "62%"] as const;

/**
 * Cold-boot silhouette of a JuisciGPT conversation (`GPTConversationPage` and
 * its room twin) — the shape a deep link into an EXISTING conversation lands
 * on: the mobile conversation header, the Answer/Sources pill, one Q/R turn,
 * and the composer.
 *
 * Distinct from `JuisciGPTBootSkeleton`, which mirrors the LANDING (greeting +
 * conversation starters + navbar). A conversation has neither, so booting into
 * one behind the landing silhouette reads as the wrong page for the whole boot
 * window. Pure render (no queries, no auth reads), like every boot skeleton.
 */
export default function GPTConversationBootSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col bg-ds-cream">
      {/* `GptConversationHeader` under the ScrollableLayout header slot (which
          owns the safe area and the translucent white): back chevron, centered
          title, new-conversation action. 24px glyphs on a `py-3` row. */}
      <div
        className="bg-white/90"
        style={{ paddingTop: "var(--safe-area-top)" }}
      >
        <div className="border-b border-secondary-soft-yellow-grey px-3.5 py-3">
          <div className="relative flex items-center justify-between">
            <Skeleton $variant="rounded" width={24} height={24} />
            {/* The title is `text-sm` (14/20), truncated at 55% of the row. */}
            <div className="absolute left-1/2 -translate-x-1/2">
              <SkeletonTextLine width={168} fontSize={14} lineHeight={20} />
            </div>
            <Skeleton $variant="rounded" width={24} height={24} />
          </div>
        </div>
      </div>

      {/* Answer / Sources pill. On the page it is FIXED at `--safe-area-top +
          56px` and the transcript reserves a 48px spacer for it; here it sits in
          the flow, so `pt-2` under the 49px header lands it on the same line.
          Drawn even though the real pill only appears once a turn has sources
          (`showTabs`): a grounded answer is the norm on this surface, and the
          alternative is a 44px hole at the top of the silhouette. */}
      <div className="flex justify-center pt-2">
        <div className="flex items-center gap-1.5 rounded-ds-button-lg bg-white/84 p-1 shadow-[0px_2px_8px_rgba(0,0,0,0.08)] backdrop-blur-md">
          {/* Each segment: icon (size-4) + a `text-footnote` label inside
              `gap-1.5 px-3 py-2` — 36px tall, "Sources (n)" the wider of the two. */}
          <Skeleton $variant="rounded" width={98} height={36} />
          <Skeleton $variant="rounded" width={122} height={36} />
        </div>
      </div>

      {/* Transcript column — `GPTConversationWrapper`'s `gap-6` stack. Its 32px
          top padding is measured from the pill's line, which the flow above
          already advanced 4px past, so `pt-7` lands the first turn where the
          page will. */}
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-5 pt-7">
        {/* The question, as the right-aligned user bubble (`ChatMessageCard`):
            two `text-ui-body` lines inside `px-4.5 py-3`. */}
        <Skeleton className="self-end rounded-2xl" width={240} height={64} />

        {/* The answer: its grounding row, the paragraph, then the action bar. */}
        <div className="flex flex-col gap-3">
          {/* `ChatActionsDropdown` — its 24px-tall header row: the
              "n searches · n sources" `text-footnote` summary on the left, the
              duration and its chevron on the right. */}
          <div className="flex h-6 items-center justify-between">
            <SkeletonTextLine width={132} fontSize={13} lineHeight={20} />
            <SkeletonTextLine width={38} fontSize={13} lineHeight={20} />
          </div>

          {ANSWER_LINE_WIDTHS.map((width, index) => (
            <SkeletonTextLine
              key={index}
              width={width}
              fontSize={15}
              lineHeight={22}
            />
          ))}

          {/* `ChatActions`: copy / share / regenerate at `size-5.25` on a
              `gap-3` row (the play + thumbs buttons are hidden). */}
          <div className="flex items-center gap-3">
            <Skeleton $variant="rounded" width={21} height={21} />
            <Skeleton $variant="rounded" width={21} height={21} />
            <Skeleton $variant="rounded" width={21} height={21} />
          </div>
        </div>

        {/* The AI disclaimer closing the transcript — a centered 10px line. */}
        <div className="flex justify-center">
          <SkeletonTextLine width={232} fontSize={10} lineHeight={16} />
        </div>
      </div>

      {/* Composer band — fixed at the bottom of the viewport on the page, here
          it closes the column. `pb` mirrors the band's own safe-area reserve. */}
      <div className="px-5 pt-2.5 pb-[calc(16px+var(--safe-area-bottom)/2)]">
        {/* `ChatInput`: rounded-2xl card, one 21px text line under `px-4 py-3`,
            then the 34px actions row — "+" and the mode picker on
            the left, mic + send on the right. */}
        <div className="flex flex-col rounded-2xl bg-[rgba(253,253,253,0.84)] shadow-[0px_2px_8px_rgba(0,0,0,0.08)]">
          <div className="px-4 py-3">
            <SkeletonTextLine width={104} fontSize={15} lineHeight={21} />
          </div>
          <div className="flex items-center justify-between p-2">
            <div className="flex items-center gap-1">
              <Skeleton $variant="rounded" width={34} height={34} />
              <Skeleton $variant="rounded" width={80} height={32} />
            </div>
            <div className="flex items-center gap-1">
              <Skeleton $variant="rounded" width={34} height={34} />
              <Skeleton $variant="rounded" width={34} height={34} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
